import React, { useState, useEffect } from 'react';
import { X, Maximize2, Target } from 'lucide-react';
import { useCategories } from '../hooks/useCategories';
import { useCurrentView } from '../hooks/useCurrentView';
import { useScheduleStore } from '../stores/scheduleStore';
import { useEventStore } from '../stores/eventStore';
import { useFocusSessionStore } from '../stores/focusSessionStore';
import { formatDateToLocal } from '../utils/date';

const toMin = (t: string): number => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

/**
 * Slim header pill for a minimized Focus session. Recomputes the current /
 * next schedule block from the live schedule + clock (Focus owns no timer
 * state); click to re-open Focus, X to end the session.
 */
export function FocusSessionWidget() {
  const [now, setNow] = useState(new Date());
  const stop = useFocusSessionStore(s => s.stop);
  const { setCurrentView } = useCurrentView();
  const { getCategoryColor } = useCategories();
  const getCurrentSchedule = useScheduleStore(s => s.getCurrentSchedule);
  const { getEventsByDate } = useEventStore();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const today = formatDateToLocal(new Date());
  const schedule = getCurrentSchedule();
  const blocks = (schedule?.blocks ?? []).filter(b => !b.prayer && !b.voluntary);
  const events = getEventsByDate(today);
  const nowMin = now.getHours() * 60 + now.getMinutes();

  type Item = { name: string; category: string; start: number; end: number };
  const items: Item[] = [
    ...blocks.map(b => ({ name: b.name, category: b.category, start: toMin(b.startTime), end: toMin(b.endTime) })),
    ...events.map(e => ({ name: e.title, category: e.category, start: toMin(e.startTime), end: toMin(e.endTime) })),
  ];
  const current = items.filter(i => nowMin >= i.start && nowMin < i.end).sort((a, b) => b.start - a.start)[0] ?? null;
  const next = items.filter(i => i.start > nowMin).sort((a, b) => a.start - b.start)[0] ?? null;

  const fmtLeft = (targetMin: number): string => {
    const target = new Date(now);
    target.setHours(0, 0, 0, 0);
    target.setMinutes(targetMin);
    const diff = Math.max(0, target.getTime() - now.getTime());
    const totalSec = Math.floor(diff / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}:${s.toString().padStart(2, '0')}`;
    return `${s}s`;
  };

  let label: string;
  let timeText: string;
  let color: string;
  if (current) {
    color = getCategoryColor(current.category);
    label = current.name;
    timeText = `${fmtLeft(current.end)} left`;
  } else if (next) {
    color = getCategoryColor(next.category);
    label = next.name;
    timeText = `in ${fmtLeft(next.start)}`;
  } else {
    color = getCategoryColor('personal');
    label = 'Focus';
    timeText = 'No active block';
  }

  const expand = () => setCurrentView('current');

  return (
    <div
      className="flex w-fit max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 cursor-pointer hover:brightness-110 transition-all"
      style={{ backgroundColor: `${color}14`, borderColor: color }}
      onClick={expand}
    >
      <span className="flex items-center justify-center rounded-full p-1 shrink-0" style={{ backgroundColor: `${color}26` }}>
        <Target className="w-3 h-3" style={{ color }} />
      </span>
      <span className="text-xs font-semibold text-foreground truncate max-w-[110px]">{label}</span>
      <span className="text-muted-foreground text-xs">·</span>
      <span className="font-mono text-xs text-foreground tabular-nums whitespace-nowrap">{timeText}</span>
      <button
        onClick={(e) => { e.stopPropagation(); expand(); }}
        className="hidden sm:flex p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        title="Expand"
      >
        <Maximize2 className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); stop(); }}
        className="p-1 rounded-full hover:bg-black/10 transition-colors shrink-0"
        title="End focus"
      >
        <X className="w-3.5 h-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
